import { Component, Input, Output, EventEmitter, OnInit, forwardRef, ElementRef, ViewChild, Renderer2, HostBinding, ChangeDetectorRef, SimpleChanges, Injector, InjectFlags, HostListener } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormControl, NgControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

export interface ResultGroup<T extends object = Record<string, unknown>> {
  label: string;
  items: T[];
}

@Component({
  selector: 'app-reusable-searcher',
  templateUrl: './reusable-searcher.component.html',
  styleUrls: ['./reusable-searcher.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ReusableSearcherComponent),
      multi: true
    }
  ]
})
export class ReusableSearcherComponent<T extends object = Record<string, unknown>> implements OnInit, ControlValueAccessor {
  @Input() placeholder: string = 'Buscar...';
  @Input() minChars: number = 1;
  @Input() debounceTime: number = 300;
  @Input() displayField: string = 'name';
  @Input() valueField: string = 'id';
  @Input() required: boolean = false;
  @Input() disabled: boolean = true;
  @Input() invalid: boolean | undefined = false;
  @Input() touched: boolean | undefined = false;
  @Input() showAbove: boolean = true;
  @Input() clearOnSelect: boolean = false;
  @Input() groupByField: string = '';
  @Input() groupTranslatePrefix: string = '';
  @Input() ungroupedLabelKey: string = '';
  @Input() showWarning: boolean = true;

  @Output() search = new EventEmitter<string>();
  @Output() itemSelected = new EventEmitter<T>();
  @Output() groupSelected = new EventEmitter<T[]>();

  @ViewChild('searchInput') searchInput!: ElementRef;
  @ViewChild('resultsContainer') resultsContainer!: ElementRef;

  searchControl = new FormControl('');
  showResults: boolean = false;
  results: T[] = [];
  @Input() selectedItem: T | null = null;
  isTouched: boolean = false;
  @Input() isLoading: boolean = false;
  hasSearched: boolean = false;
  showWarningMessage: boolean = false;
  groupedResults: ResultGroup<T>[] = [];
  private _ngControl: NgControl | null = null;

  onChange: (value: unknown) => void = () => { };
  onTouched: () => void = () => { };

  constructor(
    private readonly renderer: Renderer2,
    private readonly el: ElementRef,
    private readonly cdr: ChangeDetectorRef,
    private readonly translate: TranslateService,
    private readonly injector: Injector
  ) { }

  private get ngControl(): NgControl | null {
    if (this._ngControl === null) {
      this._ngControl = this.injector.get(NgControl, null, InjectFlags.Self | InjectFlags.Optional);
      if (this._ngControl) {
        this._ngControl.valueAccessor = this;
      }
    }

    return this._ngControl;
  }

  get shouldShowError(): boolean {
    const controlInvalid = this.ngControl?.invalid ?? this.invalid ?? false;
    const controlTouched = this.ngControl?.touched ?? this.touched ?? false;
    return !!((controlInvalid || (this.required && this.isTouched && !this.selectedItem)) && (controlTouched || this.isTouched));
  }

  ngOnInit(): void {
    this.searchControl.valueChanges.pipe(
      debounceTime(this.debounceTime),
      distinctUntilChanged()
    ).subscribe(value => {
      if (value && value.length >= this.minChars) {
        this.onSearch(value);
      } else {
        this.isLoading = true;
        this.search.emit('');
        this.hasSearched = true;
        this.showWarningMessage = this.showWarning;
      }
    });
  }

  /**
   * Safely accesses a property from an object, returning unknown type
   */
  private getPropertyValue(obj: T | null | undefined, property: string): unknown {
    if (!obj || typeof obj !== 'object') {
      return undefined;
    }
    return (obj as Record<string, unknown>)[property];
  }

  ngOnChanges(change: SimpleChanges) {
    if (change["selectedItem"] && change["selectedItem"].previousValue !== this.selectedItem) {
      this.clearOnSelect = false;
      this.selectItem(this.selectedItem)
    }
  }

  setResults(results: any[]): void {
    this.results = results;
    if (this.groupByField) {
      this.computeGroupedResults();
    }
    this.isLoading = false;
    const isInputFocused = document.activeElement === this.searchInput?.nativeElement;
    this.showResults = isInputFocused && (results.length > 0);

    if (this.showResults) {
      setTimeout(() => {
        this.checkPosition();
      });
    }
    this.cdr.detectChanges();
  }

  private computeGroupedResults(): void {
    const groups = new Map<string, T[]>();
    const ungroupedKey = '__ungrouped__';

    for (const item of this.results) {
      const groupValue = this.getPropertyValue(item, this.groupByField);
      const groupKey = String(groupValue || ungroupedKey);
      if (!groups.has(groupKey)) groups.set(groupKey, []);
      groups.get(groupKey)!.push(item);
    }

    this.groupedResults = Array.from(groups.entries())
      .sort((a, b) => {
        if (a[0] === ungroupedKey) return 1;
        if (b[0] === ungroupedKey) return -1;
        return a[0].localeCompare(b[0]);
      })
      .map(([key, items]) => ({
        label: this.resolveGroupLabel(key, ungroupedKey),
        items
      }));
  }

  private resolveGroupLabel(key: string, ungroupedKey: string): string {
    if (key === ungroupedKey) {
      return this.ungroupedLabelKey
        ? this.translate.instant(this.ungroupedLabelKey)
        : '';
    }

    if (this.groupTranslatePrefix) {
      const translated = this.translate.instant(this.groupTranslatePrefix + key);
      if (translated !== this.groupTranslatePrefix + key) {
        return translated;
      }
    }

    return key;
  }

  checkPosition(): void {
    if (!this.searchInput || !this.resultsContainer) return;

    if (this.showAbove) {
      this.renderer.addClass(this.resultsContainer.nativeElement, 'show-above');
      this.renderer.removeClass(this.resultsContainer.nativeElement, 'show-below');
    } else {
      this.renderer.addClass(this.resultsContainer.nativeElement, 'show-below');
      this.renderer.removeClass(this.resultsContainer.nativeElement, 'show-above');
    }
  }

  selectItem(item: any): void {
    if (!item) { this.clearSearch(); return }
    this.selectedItem = item;
    this.itemSelected.emit(item);
    this.onChange(item[this.valueField]);
    this.markAsTouched();
    if (this.clearOnSelect) {
      this.searchControl.setValue('', { emitEvent: false });
    } else {
      this.searchControl.setValue(item[this.displayField], { emitEvent: false });
      this.showResults = false;
    }
  }

  onItemMousedown(event: MouseEvent, item: any): void {
    event.preventDefault();
    event.stopPropagation();
    this.selectItem(item);
  }

  onFocus(): void {
    this.openDropdown();
  }

  onInputClick(): void {
    this.openDropdown();
  }

  private openDropdown(): void {
    if (this.results.length > 0) {
      this.showResults = true;
      setTimeout(() => {
        this.checkPosition();
      });
    } else {
      this.isLoading = true;
      this.search.emit('');
      this.hasSearched = true;
      this.showWarningMessage = this.showWarning;
    }
  }

  onBlur(): void {
    this.markAsTouched();

    setTimeout(() => {
      this.showResults = false;
    }, 200);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.showResults) {
      return;
    }

    const clickedInside = this.el.nativeElement.contains(event.target as Node | null);
    if (!clickedInside) {
      this.showResults = false;
      this.cdr.detectChanges();
    }
  }

  markAsTouched(): void {
    if (!this.isTouched) {
      this.isTouched = true;
      this.onTouched();
    }
  }

  writeValue(value: any): void {
    if (value === null || value === undefined || value === '') {
      this.searchControl.setValue('', { emitEvent: false });
      this.selectedItem = null;
      return;
    }

    if (typeof value === 'object') {
      this.selectedItem = value;
      this.searchControl.setValue(this.getDisplayValue(value), { emitEvent: false });
      return;
    }

    const hasSelectedMatch =
      !!this.selectedItem &&
      this.getPropertyValue(this.selectedItem, this.valueField) !== undefined &&
      String(this.getPropertyValue(this.selectedItem, this.valueField)) === String(value);

    if (hasSelectedMatch) {
      if (this.selectedItem) {
        this.searchControl.setValue(this.getDisplayValue(this.selectedItem), { emitEvent: false });
      }
      return;
    }

    const current = (this.searchControl.value ?? '').toString().trim();
    if (!current) {
      this.searchControl.setValue(String(value), { emitEvent: false });
    }
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.searchControl.disable();
    } else {
      this.searchControl.enable();
    }
  }

  getDisplayValue(item: T): string {
    const displayValue = this.getPropertyValue(item, this.displayField);
    return typeof displayValue === 'string' ? displayValue : String(displayValue || '');
  }

  isItemSelected(item: T): boolean {
    if (!this.selectedItem || !item) {
      return false;
    }

    const selectedValue = this.getPropertyValue(this.selectedItem, this.valueField);
    const itemValue = this.getPropertyValue(item, this.valueField);

    return selectedValue !== undefined && itemValue !== undefined && String(selectedValue) === String(itemValue);
  }

  selectGroup(group: ResultGroup<T>): void {
    this.groupSelected.emit(group.items);
    if (this.clearOnSelect) {
      this.searchControl.setValue('', { emitEvent: false });
    }
  }

  onGroupMousedown(event: MouseEvent, group: ResultGroup<T>): void {
    event.preventDefault();
    event.stopPropagation();
    this.selectGroup(group);
  }

  clearSearch(): void {
    this.searchControl.setValue('');
    this.selectedItem = null;
    this.results = [];
    this.showResults = false;
    this.hasSearched = false;
    this.onChange(null);
    this.markAsTouched();
  }

  async onSearch(value: string): Promise<void> {
    if (value && value.length >= this.minChars) {
      this.isLoading = true;
      await this.search.emit(value);
      this.hasSearched = true;
      this.showWarningMessage = false;
    }
  }
}
